import React, { useState, useEffect, useRef } from 'react'
import axios from 'axios'
import { toast } from 'react-hot-toast'
import { Link } from 'react-router-dom'
import {
  Bot, Database, Boxes, Route, Workflow, Plug, Terminal, Cpu, CheckCircle2, Circle,
  AlertTriangle, ArrowRight, Sparkles, Users, Loader2, Crown, Send, Play, MessageSquare,
} from 'lucide-react'
import { getSession } from '../lib/session'
import AgentMesh from '../components/AgentMesh'
import AgentAsk from '../components/AgentAsk'

const ICONS = { bot: Bot, database: Database, boxes: Boxes, route: Route, workflow: Workflow, plug: Plug, terminal: Terminal }
// Where each agent's manager page lives, so a card can deep-link to "open it".
const MANAGE = { answers: '/answers', sync: '/sync', rag: '/rag', intake: '/intake', orchestration: '/workflows', middleware: '/adapters', agent: '/' }

// Services the Chief can dispatch a direct, one-shot action for (server: SERVICE_ACTIONS
// in routes/agent.js). Only these get a "Run" button on their card.
const RUNNABLE = new Set(['connector-health', 'growth', 'marketing-intel', 'notifications', 'sync'])

const STATUS = {
  active: { dot: 'bg-green-500', ring: 'ring-green-500/20', label: 'Active', text: 'text-green-700' },
  idle: { dot: 'bg-gray-300', ring: 'ring-gray-300/30', label: 'Idle', text: 'text-gray-500' },
  error: { dot: 'bg-red-500', ring: 'ring-red-500/20', label: 'Needs attention', text: 'text-red-600' },
}

const ago = (t) => {
  if (!t) return 'never'
  const s = Math.floor((Date.now() - new Date(t).getTime()) / 1000)
  if (s < 60) return 'just now'
  if (s < 3600) return `${Math.floor(s / 60)}m ago`
  if (s < 86400) return `${Math.floor(s / 3600)}h ago`
  return `${Math.floor(s / 86400)}d ago`
}

const CHIEF_SUGGESTIONS = [
  'How are we doing across the business?',
  'Check connector health',
  'Scan for friction',
  'Send me a digest',
]

/* ────────────────────────────────────────────────────────────────────────────
   Chief Agent console — a chat hero that talks to the Chief (POST /api/v1/agent/
   chief). The Chief answers holistically or dispatches a sub-agent inline; when
   it acts, action.ran is true and we surface a "ran: <agent>" chip.
   `seed` (a {text,nonce} bump from a card's "Ask the chief about this" link)
   pre-fills the input and focuses it.
──────────────────────────────────────────────────────────────────────────── */
function ChiefConsole({ total, active, seed }) {
  const [msgs, setMsgs] = useState([{
    role: 'bot',
    text: "I'm your Chief Agent — I oversee every agent in your workspace. Ask me anything across your business, or tell me to act. I'll answer holistically or dispatch the right sub-agent for you.",
  }])
  const [input, setInput] = useState('')
  const [busy, setBusy] = useState(false)
  const feedRef = useRef(null)
  const inputRef = useRef(null)

  useEffect(() => {
    if (feedRef.current) feedRef.current.scrollTop = feedRef.current.scrollHeight
  }, [msgs, busy])

  useEffect(() => {
    if (seed?.text) {
      setInput(seed.text)
      inputRef.current?.focus()
    }
  }, [seed?.nonce])

  async function send(text) {
    const message = String(text ?? input).trim()
    if (!message || busy) return
    setInput('')
    setMsgs((m) => [...m, { role: 'me', text: message }])
    setBusy(true)
    try {
      const { data } = await axios.post('/api/v1/agent/chief', { message })
      setMsgs((m) => [...m, {
        role: 'bot',
        text: data?.reply || 'Done.',
        action: data?.action?.ran ? data.action : null,
        links: Array.isArray(data?.links) ? data.links : [],
        sources: Array.isArray(data?.sources) ? data.sources : [],
        confidence: typeof data?.confidence === 'number' ? data.confidence : null,
      }])
    } catch (err) {
      setMsgs((m) => [...m, {
        role: 'bot', error: true,
        text: err?.response?.data?.error || "I couldn't reach the mesh just now. Try again in a moment.",
      }])
    } finally {
      setBusy(false)
    }
  }

  return (
    <div className="ca-root">
      <div className="ca-console">
        <div className="ca-head">
          <div className="ca-av"><Crown size={22} /><Sparkles size={11} className="ca-spark" /></div>
          <div className="ca-htxt">
            <h3>Chief Agent</h3>
            <p>oversees {total} agent{total === 1 ? '' : 's'} · {active} active</p>
          </div>
          <span className="ca-livepill"><span className="ca-livedot" /> Online</span>
        </div>

        <div className="ca-feed" ref={feedRef}>
          {msgs.map((m, i) => (
            <div key={i} className={`ca-msg ${m.role === 'me' ? 'me' : 'bot'} ${m.error ? 'err' : ''}`}>
              <div className="ca-msgtext">{m.text}</div>
              {m.action && (
                <div className="ca-ran">
                  <CheckCircle2 size={13} /> ran: <b>{m.action.agent}</b> — {m.action.summary}
                </div>
              )}
              {m.links?.length > 0 && (
                <div className="ca-links">
                  {m.links.map((l, j) => (
                    <div key={j} className="ca-link">
                      <a href={l.href} onClick={(e) => { e.preventDefault(); window.location.assign(l.href) }} className="ca-linkbtn">
                        {l.label} <ArrowRight size={13} />
                      </a>
                      {Array.isArray(l.steps) && l.steps.length > 0 && (
                        <ol className="ca-steps">{l.steps.map((s, k) => <li key={k}>{s}</li>)}</ol>
                      )}
                    </div>
                  ))}
                </div>
              )}
              {m.sources?.length > 0 && (
                <div className="ca-src">
                  {m.sources.map((s, j) => <span key={j}>{s}</span>)}
                </div>
              )}
              {typeof m.confidence === 'number' && m.confidence > 0 && (
                <div className="ca-conf">Confidence {Math.round(m.confidence * 100)}%</div>
              )}
            </div>
          ))}
          {busy && (
            <div className="ca-typing"><i /><i /><i /></div>
          )}
        </div>

        <div className="ca-sugg">
          {CHIEF_SUGGESTIONS.map((q) => (
            <button key={q} type="button" disabled={busy} onClick={() => send(q)}>{q}</button>
          ))}
        </div>

        <form className="ca-in" onSubmit={(e) => { e.preventDefault(); send() }}>
          <input
            ref={inputRef}
            value={input}
            onChange={(e) => setInput(e.target.value)}
            placeholder="Ask the Chief anything, or tell it to act…"
            disabled={busy}
          />
          <button type="submit" disabled={busy || !input.trim()} aria-label="Send">
            {busy ? <Loader2 size={18} className="ca-spin" /> : <Send size={18} />}
          </button>
        </form>
      </div>

      <style>{CONSOLE_CSS}</style>
    </div>
  )
}

/* Recent agent activity — reads the agent_runs audit log (GET /api/v1/agent/runs)
   and shows the last few delegated runs (agent · action · summary · time). Proves
   the mesh logs what it does. Graceful empty state. */
function RecentActivity() {
  const [runs, setRuns] = useState(null)

  useEffect(() => {
    let live = true
    axios.get('/api/v1/agent/runs', { params: { limit: 8 } })
      .then(r => { if (live) setRuns(Array.isArray(r.data?.runs) ? r.data.runs : []) })
      .catch(() => { if (live) setRuns([]) })
    return () => { live = false }
  }, [])

  if (runs === null) return null

  return (
    <div className="card">
      <div className="flex items-center gap-2 mb-3">
        <MessageSquare size={15} className="text-primary-600" />
        <h3 className="font-semibold text-gray-900 text-sm">Recent agent activity</h3>
        <span className="text-[11px] text-gray-400">delegation, logged</span>
      </div>
      {runs.length === 0 ? (
        <p className="text-sm text-gray-500">No agent runs yet — talk to the Chief or run an agent, and its activity shows up here.</p>
      ) : (
        <div className="divide-y divide-gray-100">
          {runs.map((r) => {
            const ok = r.status === 'ok' || r.status === 'success' || r.status === 'done'
            const name = r.sub_agent || r.agent || 'agent'
            return (
              <div key={r.id} className="flex items-center gap-2 py-2 text-sm">
                <span className={`w-1.5 h-1.5 rounded-full shrink-0 ${ok ? 'bg-green-500' : r.status === 'error' ? 'bg-red-500' : 'bg-gray-300'}`} />
                <span className="font-semibold text-gray-900 shrink-0">{name}</span>
                {r.action && <span className="text-[11px] uppercase tracking-wide bg-gray-100 text-gray-500 rounded px-1.5 py-0.5 shrink-0">{r.action}</span>}
                <span className="text-gray-600 truncate flex-1">{r.summary || '—'}</span>
                <span className="text-xs text-gray-400 shrink-0">{ago(r.created_at)}</span>
              </div>
            )
          })}
        </div>
      )}
    </div>
  )
}

/* One agent card — same layout as before, plus a "Run" action (for dispatchable
   services) and an "Ask the chief about this" seed link. */
function AgentCard({ a, onSeedChief }) {
  const [running, setRunning] = useState(false)
  const [asking, setAsking] = useState(false)
  const Icon = ICONS[a.icon] || Bot
  const st = STATUS[a.status] || STATUS.idle
  const canRun = RUNNABLE.has(a.service)

  async function run() {
    if (running) return
    setRunning(true)
    try {
      const { data } = await axios.post(`/api/v1/agent/${a.service}/run`, {})
      if (data?.ok) toast.success(`${a.agentName}: ${data.summary || 'Done.'}`)
      else toast(`${a.agentName} has no direct action to run.`)
    } catch (err) {
      toast.error(err?.response?.data?.error || `Couldn't run ${a.agentName}.`)
    } finally {
      setRunning(false)
    }
  }

  return (
    <div className="card flex flex-col gap-4">
      {/* Header */}
      <div className="flex items-start gap-3">
        <div className={`w-11 h-11 rounded-xl bg-primary-50 text-primary-600 flex items-center justify-center shrink-0 ring-4 ${st.ring}`}>
          <Icon size={22} />
        </div>
        <div className="min-w-0 flex-1">
          <div className="flex items-center gap-2 flex-wrap">
            <h3 className="font-semibold text-gray-900">{a.agentName}</h3>
            <span className="text-[11px] uppercase tracking-wide bg-gray-100 text-gray-500 rounded px-1.5 py-0.5">{a.service}</span>
          </div>
          <p className="text-[11px] text-gray-400 font-mono">{a.className}</p>
        </div>
        <span className={`flex items-center gap-1.5 text-xs font-medium ${st.text} shrink-0`}>
          <span className={`w-2 h-2 rounded-full ${a.status === 'active' ? 'animate-pulse' : ''} ${st.dot}`} /> {st.label}
        </span>
      </div>

      {/* What it does */}
      <p className="text-sm text-gray-600 leading-relaxed">{a.role}</p>
      <p className="text-xs text-gray-400 flex items-center gap-1.5"><Sparkles size={12} /> {a.trigger}</p>

      {a.error && (
        <div className="text-xs text-red-600 bg-red-50 border border-red-100 rounded-lg px-3 py-2 flex items-start gap-2">
          <AlertTriangle size={14} className="mt-0.5 shrink-0" /> {a.error}
        </div>
      )}

      {/* Live activity */}
      {a.metrics?.length > 0 && (
        <div className="grid grid-cols-3 gap-2">
          {a.metrics.map((m, i) => (
            <div key={i} className="bg-gray-50 rounded-lg p-2.5 text-center">
              <div className="text-lg font-bold text-gray-900 tabular-nums">{typeof m.value === 'number' ? Number(m.value).toLocaleString() : m.value}</div>
              <div className="text-[10px] text-gray-500 leading-tight">{m.label}</div>
            </div>
          ))}
        </div>
      )}

      {/* What it can provide */}
      <div>
        <p className="text-[11px] uppercase tracking-wide text-gray-400 font-semibold mb-1.5">What it can provide</p>
        <div className="flex flex-wrap gap-1.5">
          {a.provides.map((p, i) => (
            <span key={i} className="text-[11px] bg-primary-50 text-primary-700 rounded-full px-2 py-0.5">{p}</span>
          ))}
        </div>
      </div>

      {/* Swarm (Intake) */}
      {a.swarm?.length > 0 && (
        <div>
          <p className="text-[11px] uppercase tracking-wide text-gray-400 font-semibold mb-2 flex items-center gap-1.5"><Users size={12} /> The swarm — sub-agents that run in sequence</p>
          <div className="space-y-1.5">
            {a.swarm.map((s, i) => (
              <div key={i} className="flex items-center gap-2 text-xs">
                <span className={`w-5 h-5 rounded-full flex items-center justify-center shrink-0 text-[10px] font-bold ${s.specialty ? 'bg-purple-100 text-purple-600' : 'bg-primary-100 text-primary-700'}`}>{i + 1}</span>
                <span className="font-medium text-gray-800">{s.name}</span>
                <span className="text-gray-400 truncate">— {s.does}</span>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Actions */}
      <div className="flex items-center gap-2 flex-wrap mt-auto pt-3 border-t border-gray-100">
        {canRun && (
          <button
            type="button"
            onClick={run}
            disabled={running}
            className="inline-flex items-center gap-1.5 text-xs font-semibold text-white bg-primary-600 hover:bg-primary-700 disabled:opacity-60 disabled:cursor-not-allowed rounded-lg px-3 py-1.5"
          >
            {running ? <Loader2 size={13} className="animate-spin" /> : <Play size={13} />}
            {running ? 'Running…' : 'Run'}
          </button>
        )}
        <button
          type="button"
          onClick={() => setAsking((v) => !v)}
          className="inline-flex items-center gap-1.5 text-xs font-semibold text-primary-600 hover:text-primary-700"
        >
          <MessageSquare size={13} /> {asking ? 'Hide chat' : 'Ask this agent'}
        </button>
        <Link to={MANAGE[a.service] || '/'} className="ml-auto text-xs text-primary-600 hover:underline flex items-center gap-1">
          Open {a.service} <ArrowRight size={12} />
        </Link>
      </div>

      {asking && (
        <AgentAsk
          service={a.service}
          agentName={a.agentName}
          intro={a.role}
          starters={[`How is my ${a.agentName} doing?`, 'What should I do next?']}
        />
      )}

      <div className="text-[11px] text-gray-400">Last active {ago(a.lastActiveAt)}</div>
    </div>
  )
}

export default function Agents() {
  const session = getSession()
  const [data, setData] = useState(null)
  const [loading, setLoading] = useState(true)
  const [seed, setSeed] = useState(null)
  const consoleRef = useRef(null)

  useEffect(() => {
    if (!session?.namespaceId) return
    axios.get(`/api/v1/tenants/${session.namespaceId}/agents`)
      .then(r => setData(r.data)).catch(() => setData({ agents: [], count: 0 }))
      .finally(() => setLoading(false))
  }, [session?.namespaceId])

  const agents = data?.agents || []
  const activeCount = agents.filter(a => a.status === 'active').length
  // The Chief oversees every OTHER agent (it's included first in the list).
  const overseen = Math.max(agents.length - 1, 0)
  // Non-chief cards render below the console.
  const cards = agents.filter(a => a.service !== 'chief')

  function seedChief(text) {
    setSeed({ text, nonce: Date.now() })
    consoleRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' })
  }

  return (
    <div className="space-y-6">
      <div className="flex items-start justify-between flex-wrap gap-3">
        <div>
          <h2 className="text-2xl font-bold text-gray-900 flex items-center gap-2"><Cpu size={24} className="text-primary-600" /> Your Agents</h2>
          <p className="text-gray-500 mt-1 max-w-2xl">
            Every service you activate is run by a real agent — an on-demand worker that spins up when there’s work, does the job, and stops. Talk to the Chief to steer them all, or run any one directly.
          </p>
        </div>
        {!loading && (
          <div className="flex gap-4 text-sm">
            <div className="text-center px-3"><div className="text-2xl font-bold text-gray-900">{agents.length}</div><div className="text-xs text-gray-500">Agents</div></div>
            <div className="text-center px-3"><div className="text-2xl font-bold text-green-600">{activeCount}</div><div className="text-xs text-gray-500">Active now</div></div>
          </div>
        )}
      </div>

      {loading ? (
        <div className="flex items-center gap-2 text-gray-500 py-10"><Loader2 className="animate-spin" size={18} /> Loading your agents…</div>
      ) : agents.length === 0 ? (
        <div className="card text-center py-12">
          <Cpu size={40} className="mx-auto mb-3 text-gray-300" />
          <p className="text-gray-600 font-medium">No agents yet</p>
          <p className="text-sm text-gray-500 mt-1">Activate a service and its agent appears here.</p>
          <Link to="/" className="btn-primary inline-block mt-4 text-sm">Browse services →</Link>
        </div>
      ) : (
        <>
          {/* Chief Agent console — steer the whole mesh from one chat. */}
          <div ref={consoleRef}>
            <ChiefConsole total={overseen} active={activeCount} seed={seed} />
          </div>

          {/* Live agent mesh — the agents working on your services and how they interact. */}
          <AgentMesh
            agents={agents}
            title="Your agent mesh"
            subtitle="The agents working across your active services — how the Chief oversees and dispatches them. Click any node to see what it does."
          />

          {/* Recent agent activity — the audit trail proving delegation is logged. */}
          <RecentActivity />

          <div className="grid gap-5 lg:grid-cols-2">
            {cards.map(a => (
              <AgentCard key={a.service} a={a} onSeedChief={seedChief} />
            ))}
          </div>
        </>
      )}

      <p className="text-xs text-gray-400">
        Agents are stateless workers — they don’t idle in the background, they run on demand and their state lives in your knowledge graph. “Active” means one ran in the last 24 hours.
      </p>
    </div>
  )
}

/* Scoped, flat, light+dark styles for the Chief console. Prefix: ca-. Mirrors the
   BrainConstellation token approach (Nunito, soft shadows, theme via [data-theme]
   and prefers-color-scheme). */
const CONSOLE_CSS = `
.ca-root{
  --ca-panel:#FFFFFF; --ca-panel2:#F7F7F1; --ca-line:#ECEBE3; --ca-ink:#2E3A45;
  --ca-mut:#7C8794; --ca-dim:#AEB6BD; --ca-hub:#4E9AC4; --ca-hubfill:#EAF4FA;
  --ca-ok:#4FBE73; --ca-okfill:rgba(90,190,120,.14); --ca-okink:#3f8a43;
  --ca-errfill:#FEF2F2; --ca-errink:#c0392b; --ca-errline:#FBD9D5;
  --ca-shadow:0 1px 2px rgba(46,58,69,.05),0 10px 26px rgba(46,58,69,.08);
  font-family:Nunito,system-ui,-apple-system,sans-serif;color:var(--ca-ink);-webkit-font-smoothing:antialiased;
}
[data-theme="dark"] .ca-root{
  --ca-panel:#171B22; --ca-panel2:#12161C; --ca-line:#262C36; --ca-ink:#E7ECF1;
  --ca-mut:#95A0AD; --ca-dim:#5C6672; --ca-hub:#5FB0DB; --ca-hubfill:#132A38;
  --ca-ok:#4FBE73; --ca-okfill:rgba(90,190,120,.16); --ca-okink:#7fce83;
  --ca-errfill:#2A1516; --ca-errink:#f0897d; --ca-errline:#4A2422;
  --ca-shadow:0 1px 2px rgba(0,0,0,.3),0 10px 26px rgba(0,0,0,.4);
}
@media (prefers-color-scheme:dark){:root:not([data-theme="light"]) .ca-root{
  --ca-panel:#171B22; --ca-panel2:#12161C; --ca-line:#262C36; --ca-ink:#E7ECF1;
  --ca-mut:#95A0AD; --ca-dim:#5C6672; --ca-hub:#5FB0DB; --ca-hubfill:#132A38;
  --ca-ok:#4FBE73; --ca-okfill:rgba(90,190,120,.16); --ca-okink:#7fce83;
  --ca-errfill:#2A1516; --ca-errink:#f0897d; --ca-errline:#4A2422;
  --ca-shadow:0 1px 2px rgba(0,0,0,.3),0 10px 26px rgba(0,0,0,.4);
}}

.ca-console{background:var(--ca-panel);border:1px solid var(--ca-line);border-radius:22px;box-shadow:var(--ca-shadow);overflow:hidden;display:flex;flex-direction:column;}
.ca-head{display:flex;align-items:center;gap:13px;padding:16px 18px;border-bottom:1px solid var(--ca-line);background:var(--ca-hubfill);}
.ca-av{position:relative;width:46px;height:46px;border-radius:15px;flex:none;background:var(--ca-panel);border:2.5px solid var(--ca-hub);display:flex;align-items:center;justify-content:center;}
.ca-av svg{stroke:var(--ca-hub);fill:none;stroke-width:2;stroke-linecap:round;stroke-linejoin:round;}
.ca-av .ca-spark{position:absolute;top:-4px;right:-4px;background:var(--ca-hub);color:#fff;stroke:#fff;border-radius:50%;padding:2px;}
.ca-htxt{min-width:0;flex:1;}
.ca-htxt h3{margin:0;font-size:18px;font-weight:800;letter-spacing:-.01em;color:var(--ca-ink);}
.ca-htxt p{margin:2px 0 0;font-size:12.5px;font-weight:700;color:var(--ca-hub);}
.ca-livepill{display:inline-flex;align-items:center;gap:6px;font-size:11.5px;font-weight:800;color:var(--ca-okink);background:var(--ca-okfill);border-radius:20px;padding:5px 11px;flex:none;}
.ca-livedot{width:7px;height:7px;border-radius:50%;background:var(--ca-ok);animation:ca-pulse 1.8s ease-in-out infinite;}
@keyframes ca-pulse{0%,100%{opacity:1}50%{opacity:.3}}

.ca-feed{padding:16px 18px;display:flex;flex-direction:column;gap:12px;max-height:360px;overflow-y:auto;}
.ca-msg{max-width:85%;font-size:13.5px;font-weight:600;line-height:1.55;padding:11px 14px;border-radius:16px;}
.ca-msg.bot{align-self:flex-start;background:var(--ca-panel2);border:1px solid var(--ca-line);border-bottom-left-radius:5px;color:var(--ca-ink);}
.ca-msg.me{align-self:flex-end;background:var(--ca-hub);color:#fff;border-bottom-right-radius:5px;}
.ca-msg.err.bot{background:var(--ca-errfill);border-color:var(--ca-errline);color:var(--ca-errink);}
.ca-msgtext{white-space:pre-wrap;}
.ca-ran{margin-top:9px;display:flex;align-items:center;gap:6px;flex-wrap:wrap;font-size:11.5px;font-weight:700;color:var(--ca-okink);background:var(--ca-okfill);border-radius:10px;padding:6px 9px;}
.ca-ran svg{flex:none;stroke:var(--ca-okink);}
.ca-links{margin-top:10px;display:flex;flex-direction:column;gap:8px;}
.ca-link{background:var(--ca-panel2);border:1px solid var(--ca-line);border-radius:11px;padding:9px 11px;}
.ca-linkbtn{display:inline-flex;align-items:center;gap:6px;font-size:12.5px;font-weight:800;color:var(--ca-hub);text-decoration:none;cursor:pointer;}
.ca-linkbtn svg{stroke:var(--ca-hub);}
.ca-steps{margin:7px 0 0;padding-left:18px;font-size:12px;font-weight:600;color:var(--ca-mut);}
.ca-steps li{margin:2px 0;}
.ca-ran b{font-weight:800;}
.ca-src{margin-top:8px;display:flex;flex-wrap:wrap;gap:5px;}
.ca-src span{font-size:10.5px;font-weight:800;background:var(--ca-panel);border:1px solid var(--ca-line);color:var(--ca-mut);border-radius:7px;padding:2px 7px;}
.ca-conf{margin-top:7px;font-size:10.5px;font-weight:800;color:var(--ca-mut);text-transform:uppercase;letter-spacing:.05em;}
.ca-msg.me .ca-conf{color:rgba(255,255,255,.85);}

.ca-typing{align-self:flex-start;display:flex;gap:4px;padding:12px 14px;background:var(--ca-panel2);border:1px solid var(--ca-line);border-radius:16px;border-bottom-left-radius:5px;}
.ca-typing i{width:7px;height:7px;border-radius:50%;background:var(--ca-mut);animation:ca-blink 1.2s infinite;}
.ca-typing i:nth-child(2){animation-delay:.2s}.ca-typing i:nth-child(3){animation-delay:.4s}
@keyframes ca-blink{0%,60%,100%{opacity:.25}30%{opacity:1}}

.ca-sugg{display:flex;flex-wrap:wrap;gap:7px;padding:0 18px 10px;}
.ca-sugg button{font-size:12px;font-weight:700;color:var(--ca-hub);background:var(--ca-hubfill);border:1px solid var(--ca-line);border-radius:16px;padding:7px 12px;cursor:pointer;}
.ca-sugg button:hover{filter:brightness(.97);}
.ca-sugg button:disabled{opacity:.55;cursor:default;}

.ca-in{display:flex;gap:8px;padding:12px 14px;border-top:1px solid var(--ca-line);}
.ca-in input{flex:1;border:1px solid var(--ca-line);background:var(--ca-panel2);border-radius:12px;padding:11px 13px;font:600 13.5px Nunito,system-ui,sans-serif;color:var(--ca-ink);outline:none;}
.ca-in input:focus{border-color:var(--ca-hub);}
.ca-in input:disabled{opacity:.6;}
.ca-in button{width:46px;border:none;border-radius:12px;background:var(--ca-hub);color:#fff;cursor:pointer;display:flex;align-items:center;justify-content:center;flex:none;}
.ca-in button:disabled{opacity:.5;cursor:default;}
.ca-in button svg{stroke:#fff;fill:none;stroke-width:2;stroke-linecap:round;stroke-linejoin:round;}
.ca-spin{animation:ca-spin 1s linear infinite;}
@keyframes ca-spin{to{transform:rotate(360deg)}}
`
